import { ShoppingCart, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCartStore } from '@/stores/cart-store'
import { cn } from '@/lib/utils'
import { useState, useEffect } from 'react'

interface AddToCartButtonProps {
  productId: string
  variantId?: string | null
  quantity?: number
  customInputs?: Record<string, string>
  disabled?: boolean
  className?: string
  size?: 'default' | 'sm' | 'lg'
  variant?: 'default' | 'outline'
  showIcon?: boolean
  children?: React.ReactNode
}

export function AddToCartButton({
  productId,
  variantId = null,
  quantity = 1,
  customInputs,
  disabled,
  className,
  size = 'default',
  variant = 'default',
  showIcon = true,
  children,
}: AddToCartButtonProps) {
  const addItem = useCartStore(state => state.addItem)
  const openCart = useCartStore(state => state.openCart)
  const hasItem = useCartStore(state => state.hasItem(productId, variantId))
  const [justAdded, setJustAdded] = useState(false)

  // Reset justAdded after animation
  useEffect(() => {
    if (justAdded) {
      const timer = setTimeout(() => setJustAdded(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [justAdded])

  const handleClick = () => {
    if (disabled) return
    addItem(productId, variantId, quantity, customInputs)
    setJustAdded(true)
    // Open cart drawer after short delay for feedback
    setTimeout(() => openCart(), 300)
  }

  const buttonClasses = cn(
    variant === 'default' && 'bg-red-600 text-white hover:bg-red-700',
    variant === 'outline' && 'border-red-600 text-red-500 hover:bg-red-600 hover:text-white',
    className
  )

  return (
    <Button
      size={size}
      variant={variant === 'outline' ? 'outline' : 'default'}
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled}
    >
      {showIcon && (
        justAdded ? (
          <Check className="size-4" />
        ) : (
          <ShoppingCart className="size-4" />
        )
      )}
      {children || (
        justAdded ? 'Added!' : hasItem ? 'Add Another' : 'Add to Cart'
      )}
    </Button>
  )
}
